/**
 * Collapse/expand visibility for the flattened, level-tagged schema-control
 * lists used by the multi-sheet XBRL forms (role 8, 9, 12, 13, 15, 18, 19, 23,
 * 24, ...). Each sheet renders its `schema.controls` as a flat array; section
 * headers carry a `ui.level` (1 = outermost) and the rows that follow a header
 * are its descendants until a header of the same-or-shallower level appears.
 *
 * WHY THIS EXISTS — the bug it fixes:
 * The hand-rolled per-sheet `shouldShowRow` walked backward and recorded the
 * FIRST header it met at each level, stopping only at the sheet's root level.
 * That ignored nesting: when a deeper section (e.g. a level-4 "financing
 * activities" header) physically precedes a SHALLOWER section (a level-2
 * "Details of cash flows" header), the deeper header was wrongly attributed as
 * an ancestor of the shallower section's rows — so collapsing financing also
 * hid the Details rows.
 *
 * THE RULE — a valid ancestor chain in a flattened tree has STRICTLY
 * DECREASING levels going up. Once a shallower header is found, every deeper
 * header further back belongs to a branch that header already closed and is NOT
 * an ancestor. `minLevel` enforces exactly that, so sibling subtrees stay
 * isolated. Works with or without a level-1 root (some sheets start at level 2).
 *
 * This is a pure, DI-free helper so it unit-tests without TestBed.
 */

/**
 * Minimal shape a schema control needs to participate in visibility.
 * `ui.level` may be a number (`3`) or a `"hN"` string (`"h3"`) — some older
 * schema JSON (role 9 / 220100 / 220000) uses the string form; both normalize
 * to the same depth.
 */
export interface LeveledControl {
  qname: string;
  ui: { level?: number | string };
}

/**
 * Normalize a control level to a number. Accepts `3`, `"3"`, or `"h3"`.
 * Returns `undefined` for data rows (no level) or unrecognised values, which
 * the walk treats as "deeper than any header".
 */
export function normalizeLevel(level: number | string | undefined): number | undefined {
  if (typeof level === 'number') {
    return Number.isFinite(level) ? level : undefined;
  }
  if (typeof level === 'string') {
    const match = /^h?(\d+)$/i.exec(level.trim());
    return match ? Number(match[1]) : undefined;
  }
  return undefined;
}

/**
 * Return the qnames of `controls[index]`'s ancestor section headers — the
 * nearest preceding headers whose levels strictly decrease going up, outermost
 * last.
 *
 * The walk is seeded with the control's OWN level so:
 *  - a header row collects only strictly-shallower ancestors (a level-2 header
 *    after a level-4 header does not adopt that level-4 header), and
 *  - a data row (no level → +Infinity) collects every enclosing header.
 *
 * `isSection` is optional but important: some schemas tag DATA fields with a
 * `ui.level` for indentation (e.g. role 15's calculated "Total comprehensive
 * income" carries `level: 4`). Only true section headers define sections, so
 * pass the component's `isSectionHeader` predicate to skip leveled data fields.
 * A skipped control does NOT close the branch — the walk keeps looking for a
 * shallower *header*. When omitted, any leveled control counts (matches the
 * older sheets that had header-only levels).
 */
export function collectAncestorSectionQnames<T extends LeveledControl>(
  controls: readonly T[],
  index: number,
  isSection?: (control: T) => boolean,
): string[] {
  const ancestors: string[] = [];
  let minLevel = normalizeLevel(controls[index]?.ui.level) ?? Number.POSITIVE_INFINITY;

  // Stop once we've recorded the outermost (level-1) section; when no level-1
  // exists the guard simply lets the walk run to the start of the list.
  for (let controlIndex = index - 1; controlIndex >= 0 && minLevel > 1; controlIndex--) {
    const candidate = controls[controlIndex];
    const level = normalizeLevel(candidate?.ui.level);
    if (level != null && level < minLevel && (!isSection || isSection(candidate))) {
      minLevel = level;
      ancestors.push(candidate.qname);
    }
  }

  return ancestors;
}

/**
 * A row is visible iff every one of its true ancestor sections is expanded.
 * Pass the component's own `isSectionExpanded` (or any predicate over qnames).
 */
export function isRowVisible<T extends LeveledControl>(
  controls: readonly T[],
  index: number,
  isSectionExpanded: (qname: string) => boolean,
  isSection?: (control: T) => boolean,
): boolean {
  return collectAncestorSectionQnames(controls, index, isSection).every(
    isSectionExpanded,
  );
}

/**
 * Whether the section header at `index` actually encloses at least one row.
 *
 * In a flattened, level-tagged list a header's subtree is the contiguous run of
 * controls immediately after it, ending at the next SECTION header of the same
 * or a shallower level. So a header has children iff the very next control is
 * NOT such a closing sibling/ancestor — i.e. it is either a deeper header or any
 * data row (data rows have no level, or carry a level only for indentation, so
 * `isSection` is what closes a subtree, not the raw level).
 *
 * Used to decide whether a header should render a collapse chevron: a header
 * with nothing beneath it has nothing to toggle.
 */
export function headerHasChildren<T extends LeveledControl>(
  controls: readonly T[],
  index: number,
  isSection?: (control: T) => boolean,
): boolean {
  const headerLevel = normalizeLevel(controls[index]?.ui.level);
  if (headerLevel == null) return false;

  const next = controls[index + 1];
  if (!next) return false;

  const nextLevel = normalizeLevel(next.ui.level);
  const closesSubtree =
    nextLevel != null &&
    nextLevel <= headerLevel &&
    (!isSection || isSection(next));
  return !closesSubtree;
}

/**
 * CSS class for a section header at the given level — `header-level-N`, where N
 * is the normalized depth. Accepts a numeric level (`3`), a numeric string
 * (`"3"`), or the `"hN"` form (`"h3"`) some older schema JSON uses. A header
 * with no recognised level falls back to the outermost `header-level-1`.
 *
 * The 1:1 level→class mapping means a new header depth needs only a matching
 * CSS rule, never a code change here. Replaces the per-sheet `if (level === 1)
 * … return 'header-level-3'` ladders that were copy-pasted across the XBRL
 * sheet components and drifted (e.g. role15 had no level-6 case).
 */
export function headerLevelClass(level: number | string | undefined): string {
  const normalized = normalizeLevel(level);
  // Developer Note: the trailing `1` is the FALLBACK/default level used when a
  // header has no recognised level. To change the default, just swap this
  // number (e.g. `header-level-2`) — it is the single place that controls it.
  return normalized != null ? `header-level-${normalized}` : 'header-level-1';
}

/**
 * Per-sheet overrides for `isCollapsibleHeader`. Kept as an options object so a
 * sheet with a non-default collapse policy can tune the shared rule instead of
 * forking it (or re-introducing a drift-prone per-sheet level allow-list).
 */
export interface CollapsibleHeaderOptions {
  /**
   * Shallowest level that is collapsible. Defaults to `2`, so the level-1
   * sheet-title banner ("Statement of financial position", "Statement of cash
   * flows", …) stays a non-toggled, always-expanded banner — collapsing it
   * would just hide the whole sheet.
   *
   * Sheets that intentionally let the user collapse the entire sheet from its
   * title row (role18 Cash Flows, role19 Changes in Equity) pass `1` to opt the
   * level-1 banner back into collapsibility.
   */
  minCollapsibleLevel?: number;
}

/**
 * Whether a control is a COLLAPSIBLE section header — it should render an
 * expand/collapse chevron and toggle on click.
 *
 * The rule is structural, not an enumerated level allow-list, so it can never
 * drift as schemas gain deeper headers (the bug that left role18 level-5 and
 * role15 level-6 categories without a show/hide button):
 *
 *  1. It must be a section header (`isSection`).
 *  2. Its level must be at least `options.minCollapsibleLevel` (default 2 — see
 *     `CollapsibleHeaderOptions` for why level 1 is normally excluded, and how
 *     role18/role19 opt it back in). Seeding a header expanded is a SEPARATE
 *     concern — see `initializeExpandedSections`, which seeds ALL headers so
 *     `isRowVisible` doesn't read an un-toggled banner as collapsed and hide
 *     everything.
 *  3. It must actually enclose at least one row (`headerHasChildren`).
 */
export function isCollapsibleHeader<T extends LeveledControl>(
  controls: readonly T[],
  index: number,
  isSection: (control: T) => boolean,
  options?: CollapsibleHeaderOptions,
): boolean {
  const control = controls[index];
  if (!control || !isSection(control)) return false;

  const level = normalizeLevel(control.ui.level);
  const minCollapsibleLevel = options?.minCollapsibleLevel ?? 2;
  if (level == null || level < minCollapsibleLevel) return false;

  return headerHasChildren(controls, index, isSection);
}
